/*
 * O carregamento das telas do ramon/marketplace no desenho do tema.
 *
 * Os skeletons da extensão (MpSkeleton) desenham o leiaute do avocado: caixas
 * com borda e blocos cinza do tamanho da página. Aqui cada variante desenha o
 * leiaute que o tema dá à tela pronta (Marketplace*.less), com os mesmos
 * containers e medidas, e os blocos são os `.CanelaSkeleton` do tema (com o
 * brilho, no claro e no escuro):
 *
 * - vitrine (`cards`): a grade de cartões, com o selo, o nome, o preço e duas
 *   linhas de descrição;
 * - listas (`list`, e os skeletons próprios de pedidos, downloads, assinaturas
 *   e reembolsos): linhas da lista de discussões, com o ícone redondo, duas
 *   linhas curtas e o valor à direita;
 * - produto: a capa larga e as duas colunas (título e texto; a caixa de compra);
 * - carrinho, checkout, pedido e painel do vendedor: as colunas e os blocos
 *   de cada um.
 *
 * As variantes que o tema não redesenha (curso, pacote, vendedor, detalhe)
 * continuam as da extensão.
 */
import { override } from 'flarum/common/extend';
import { thirdParty } from '../settings';
const MODULE = 'ext:ramon/marketplace/forum/components/';

/** Um bloco do skeleton do tema, com a largura e a altura do que ele substitui. */
function b(width, height, extra = '') {
  return m("span", {
    className: `CanelaSkeleton CanelaMpSkel-b ${extra}`.trim(),
    style: {
      width,
      height: `${height}px`
    }
  });
}
function repeat(n, render) {
  return Array.from({
    length: n
  }).map((_, i) => render(i));
}

/** Larguras que variam de linha para linha, para não parecer uma régua. */
const WIDTHS = ['62%', '48%', '70%', '55%', '40%', '66%'];

/**
 * Uma barra de texto (`.CanelaSkeleton--line`): a altura de uma linha do
 * elemento em que ela está, então cai onde a linha real vai cair.
 */
function line(width) {
  return m("span", {
    className: "CanelaSkeleton CanelaSkeleton--line",
    style: {
      width
    }
  });
}
function cards(n) {
  // Os mesmos containers do cartão pronto (cabeçalho em grade, título,
  // preço, descrição de até três linhas e rodapé), com barras de texto
  // dentro deles: cada uma herda a fonte e a altura de linha do texto real.
  return m("div", {
    className: "MpShop-grid CanelaMpSkel-grid"
  }, repeat(n, i => m("div", {
    key: String(i),
    className: "MpShop-card CanelaMpSkel-card"
  }, m("div", {
    className: "MpShop-cardHead"
  }, m("div", {
    className: "MpShop-cardIcon"
  }, b('44px', 44, 'CanelaMpSkel-tile')), m("div", {
    className: "MpShop-cardMain"
  }, m("h3", {
    className: "MpShop-cardTitle"
  }, line(WIDTHS[i % WIDTHS.length]))), m("div", {
    className: "MpShop-priceBtn"
  }, line('38%'))), m("div", {
    className: "MpShop-cardDesc"
  }, line('100%'), line('92%'), line('64%')), m("div", {
    className: "MpShop-cardFoot"
  }, line('45%')))));
}
function rows(n, round = true) {
  return m("div", {
    className: "CanelaMpSkel-list"
  }, repeat(n, i => m("div", {
    key: String(i),
    className: "CanelaMpSkel-row"
  }, b('40px', 40, round ? 'CanelaMpSkel-avatar' : 'CanelaMpSkel-tile'), m("div", {
    className: "CanelaMpSkel-lines"
  }, b(WIDTHS[i % WIDTHS.length], 18), b('28%', 13)), b('84px', 20, 'CanelaMpSkel-end'))));
}
function product() {
  return m("div", {
    className: "CanelaMpSkel-product"
  }, b('64px', 14), b('100%', 280, 'CanelaMpSkel-cover'), m("div", {
    className: "MpProduct-cols"
  }, m("div", {
    className: "CanelaMpSkel-lines CanelaMpSkel-main"
  }, b('58%', 40), b('30%', 16), m("div", {
    className: "CanelaMpSkel-tabs"
  }, b('96px', 18), b('110px', 18)), repeat(5, i => b(i === 4 ? '46%' : '100%', 15))), m("div", {
    className: "CanelaMpSkel-buy"
  }, b('55%', 34), b('30%', 14), b('100%', 46, 'CanelaMpSkel-button'), b('100%', 36, 'CanelaMpSkel-button'))));
}
function summary(lines) {
  return m("div", {
    className: "CanelaMpSkel-summary"
  }, b('50%', 22), repeat(lines, () => m("div", {
    className: "CanelaMpSkel-pair"
  }, b('34%', 14), b('22%', 14))), b('100%', 46, 'CanelaMpSkel-button'));
}
function cart(n) {
  return m("div", {
    className: "CanelaMpSkel-split"
  }, m("div", {
    className: "CanelaMpSkel-list"
  }, repeat(n, i => m("div", {
    key: String(i),
    className: "CanelaMpSkel-row"
  }, b('64px', 64, 'CanelaMpSkel-tile'), m("div", {
    className: "CanelaMpSkel-lines"
  }, b(WIDTHS[i % WIDTHS.length], 18), b('24%', 13)), b('104px', 36, 'CanelaMpSkel-button'), b('80px', 20, 'CanelaMpSkel-end')))), summary(2));
}
function checkout() {
  const field = (width = '100%') => m("div", {
    className: "CanelaMpSkel-field"
  }, b('90px', 13), b(width, 42));
  return m("div", {
    className: "CanelaMpSkel-split"
  }, m("div", {
    className: "CanelaMpSkel-form"
  }, repeat(3, i => m("div", {
    key: String(i),
    className: "CanelaMpSkel-step"
  }, b('160px', 24), field(), i < 2 && field('70%')))), summary(3));
}
function order(n) {
  return m("div", {
    className: "CanelaMpSkel-order"
  }, b('64px', 14), m("div", {
    className: "CanelaMpSkel-orderHead"
  }, m("div", {
    className: "CanelaMpSkel-lines"
  }, b('38%', 32), b('46%', 14)), b('120px', 32, 'CanelaMpSkel-end')), m("div", {
    className: "CanelaMpSkel-split"
  }, rows(Math.max(2, n), false), summary(2)));
}

/** O painel do vendedor: o estado, os números de venda, a tabela e o formulário da loja. */
function store() {
  return m("div", {
    className: "CanelaMpSkel-store"
  }, m("div", {
    className: "CanelaMpSkel-pair CanelaMpSkel-band"
  }, b('60px', 18), b('64px', 22, 'CanelaMpSkel-pill')), m("div", {
    className: "CanelaMpSkel-step"
  }, b('120px', 20), b('70%', 14), b('160px', 36, 'CanelaMpSkel-button')), m("div", {
    className: "CanelaMpSkel-step"
  }, b('180px', 20), m("div", {
    className: "CanelaMpSkel-metrics"
  }, repeat(6, i => m("div", {
    key: String(i),
    className: "CanelaMpSkel-metric"
  }, b('60%', 22), b('80%', 12))))), rows(3, false), m("div", {
    className: "CanelaMpSkel-step"
  }, b('160px', 20), m("div", {
    className: "CanelaMpSkel-field"
  }, b('110px', 13), b('100%', 42)), m("div", {
    className: "CanelaMpSkel-field"
  }, b('90px', 13), b('100%', 84))));
}
const VARIANTS = {
  cards: count => cards(count ?? 6),
  list: count => rows(count ?? 5),
  product: () => product(),
  cart: count => cart(count ?? 3),
  checkout: () => checkout(),
  order: count => order(count ?? 2),
  store: () => store()
};
export default function installMarketplaceSkeletons() {
  override(`${MODULE}MpSkeleton`, 'view', function (original, vnode) {
    const variant = vnode?.attrs?.variant || 'list';
    const render = VARIANTS[variant];
    if (!thirdParty.marketplace() || !render) return original(vnode);
    return m("div", {
      className: `CanelaMpSkel CanelaMpSkel--${variant}`,
      "aria-hidden": "true"
    }, render(vnode.attrs.count));
  });

  // As listas que desenham o próprio skeleton: as linhas da lista de discussões.
  const lists = {
    MyOrdersPage: 4,
    DownloadsPage: 4,
    MySubscriptionsPage: 3,
    MyRefundRequestsPage: 3
  };
  Object.entries(lists).forEach(([page, count]) => {
    override(`${MODULE}${page}`, 'renderSkeleton', function (original) {
      if (!thirdParty.marketplace()) return original();
      return m("div", {
        className: "CanelaMpSkel CanelaMpSkel--list",
        "aria-hidden": "true"
      }, rows(count, page !== 'MySubscriptionsPage'));
    });
  });
}
flarum.reg.add('ramon-canela', 'forum/thirdparty/marketplace/skeleton', installMarketplaceSkeletons);